import { useCallback, useEffect, useState, type ReactNode } from "react";
import { get, post, type Member, type Notification } from "../lib/api.ts";

/**
 * What each kind says. A nudge's `message` is a note somebody typed, so it is quoted; for every
 * other kind it is the name of the thing as it read when the reminder was sent, so it is not.
 * A kind missing from this map is not shown: a newer server must not put a wrong sentence here.
 */
const COPY: Record<Notification["kind"], (name: string, message: string | null) => ReactNode> = {
  SYNC_NUDGE: (name, message) => (
    <>
      <strong>{name}</strong> is waiting for your footage. {message && <em>“{message}”</em>}
    </>
  ),
  TASK_CREATED: (name, message) => (
    <>
      <strong>{name}</strong> wrote a brief for {message}.
    </>
  ),
  TASK_CHANGED: (name, message) => (
    <>
      <strong>{name}</strong> changed the brief for {message}.
    </>
  ),
  REVISION_PUBLISHED: (name, message) => (
    <>
      <strong>{name}</strong> uploaded a new version of {message}.
    </>
  ),
  REVIEW_APPROVED: (name, message) => (
    <>
      <strong>{name}</strong> approved {message}.
    </>
  ),
  REVIEW_CHANGES_REQUESTED: (name, message) => (
    <>
      <strong>{name}</strong> asked for changes on {message}.
    </>
  ),
};

/** Shown only if the client has copy for the kind and, bar a nudge, the name it speaks of. */
const known = (n: Notification) =>
  Object.hasOwn(COPY, n.kind) && (n.kind === "SYNC_NUDGE" || !!n.message);

/**
 * The member's own reminders for this trip. Nobody else, including admins, can see them.
 *
 * It sits inline on the desktop Overview and on an editor's board, where it is part of what those
 * pages are for, and it is a page of its own under Settings, which is where the phone goes for it.
 * It is no longer on the Media Library: a panel of other people's asks sat on top of a wall of
 * footage, in the way of the thing the tab is for.
 */
export function Inbox({ tripId, showEmpty = false }: { tripId: string; showEmpty?: boolean }) {
  const base = `/v1/trips/${tripId}`;
  const [items, setItems] = useState<Notification[] | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const load = useCallback(async () => {
    const r = await get<{ unread: number; items: Notification[] }>(`${base}/notifications`);
    setItems(r.items);
  }, [base]);
  useEffect(() => {
    load().catch(() => undefined);
    get<{ items: Member[] }>(`${base}/members`)
      .then((m) => setMembers(m.items))
      .catch(() => undefined);
    const t = setInterval(() => load().catch(() => undefined), 15_000);
    return () => clearInterval(t);
  }, [load, base]);

  const shown = items?.filter(known) ?? [];
  // Inline, an empty inbox is nothing worth saying and the panel simply does not appear. On its
  // own page it has to speak: a row that opens a blank screen reads as broken.
  if (shown.length === 0) {
    if (!showEmpty || items === null) return null;
    return (
      <section className="panel stack" aria-label="Reminders for you">
        <h3>Reminders for you</h3>
        <p className="muted small">
          Nothing is waiting for you. Reminders land here when somebody asks you for footage, or
          when a brief or a cut you are on changes.
        </p>
      </section>
    );
  }
  const who = (id: string) => members.find((m) => m.userId === id)?.displayName ?? "Someone";
  return (
    <section className="panel stack" aria-label="Reminders for you">
      <h3>Reminders for you</h3>
      <ul className="plain">
        {shown.map((n) => (
          <li key={n.id} className="row wrap">
            <span className="grow">
              {COPY[n.kind](who(n.fromUserId), n.message)}{" "}
              <span className="muted">{new Date(n.createdAt).toLocaleString()}</span>
            </span>
            {!n.read && (
              <button
                className="button"
                onClick={() => post(`${base}/notifications/${n.id}/read`).then(load)}
              >
                Mark as read
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
